import { useState } from "react";
import { Spin } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useQuery } from "@tanstack/react-query";
import { fetchDashboard, fetchAnalytics } from "../api/auth";
import type { AnalyticsRow } from "../api/types";
import { Metric } from "../components/Metric";
import {
  DataTable,
  Empty,
  ErrorNote,
  PageHeader,
  PageShell,
  Panel,
  StatusTag,
} from "../components/Page";

export default function VehiclesAnalyticsPage() {
  const [page, setPage] = useState(1);

  const {
    data: dashboard,
    isPending: dashPending,
    error: dashError,
  } = useQuery({ queryKey: ["dashboard"], queryFn: fetchDashboard });

  const {
    data: analytics,
    isPending: anPending,
    error: anError,
  } = useQuery({
    queryKey: ["analytics", page],
    queryFn: () => fetchAnalytics(page, 20),
  });

  if (dashError || anError)
    return (
      <PageShell>
        <ErrorNote error={dashError ?? anError} />
      </PageShell>
    );

  const rows = analytics?.rows ?? [];

  const columns: ColumnsType<AnalyticsRow> = [
    {
      title: "Bike",
      dataIndex: "title",
      render: (_text, record) => (
        <div className="min-w-0">
          <div className="font-medium text-ink">{record.title}</div>
          <div className="font-mono text-[11.5px] text-ink-faint">{record.id}</div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (_text, record) => <StatusTag status={record.status} />,
    },
    { title: "Views", dataIndex: "views", align: "right", width: 90 },
    { title: "Inquiries", dataIndex: "inquiries", align: "right", width: 100 },
    {
      title: "Contact releases",
      dataIndex: "contactReleases",
      align: "right",
      width: 150,
      render: (_text, record) => (
        <span className="tnum">
          {record.contactReleases === null ? "—" : String(record.contactReleases)}
        </span>
      ),
    },
    {
      title: "Listed",
      dataIndex: "createdAt",
      width: 120,
      render: (_text, record) => (
        <span className="tnum text-ink-muted">
          {new Date(record.createdAt).toLocaleDateString()}
        </span>
      ),
    },
  ];

  return (
    <PageShell>
      <PageHeader
        eyebrow="Inventory"
        title="Analytics"
        description="How your bikes are performing, and what is not measurable yet."
      />

      {dashPending || anPending ? (
        <div className="flex justify-center py-16">
          <Spin />
        </div>
      ) : dashboard ? (
        <div className="space-y-4">
          {/* Each figure carries its coverage flag so an unwired module reads as
              "not wired yet", never as 0. */}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Metric
              label="Active listings"
              value={dashboard.activeListings}
              coverage={dashboard.coverage.activeListings}
            />
            <Metric
              label="Total views"
              value={dashboard.views}
              coverage={dashboard.coverage.views}
            />
            <Metric
              label="Inquiries"
              value={dashboard.inquiries.total}
              coverage={dashboard.coverage.inquiries}
              hint="buyers who wrote about a listing"
            />
            <Metric
              label="Contact releases"
              value={dashboard.contactReleases}
              coverage={dashboard.coverage.contactReleases}
              hint="buyers who shared their number"
            />
          </div>

          <Panel
            flush
            title="Bike-by-bike performance"
            action={
              <div className="flex flex-wrap items-center gap-2">
                {dashboard.coverage.inquiries !== "live" && (
                  <span className="rounded-full bg-signal-wash px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-signal-ink">
                    Inquiries not wired
                  </span>
                )}
                {dashboard.coverage.contactReleases !== "live" && (
                  <span className="rounded-full bg-signal-wash px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-signal-ink">
                    Releases not wired
                  </span>
                )}
              </div>
            }
          >
            {rows.length === 0 ? (
              <Empty title="No performance data yet">
                Your bikes will appear here once they start receiving views.
              </Empty>
            ) : (
              <DataTable<AnalyticsRow>
                columns={columns}
                rows={rows}
                total={analytics?.total}
                page={page}
                pageSize={20}
                onPageChange={setPage}
              />
            )}
          </Panel>

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Listings by status">
              {Object.keys(dashboard.listingsByStatus).length === 0 ? (
                <Empty title="Nothing listed yet">
                  Statuses appear once you have a bike on the board.
                </Empty>
              ) : (
                <ul className="text-sm">
                  {Object.entries(dashboard.listingsByStatus).map(([status, count]) => (
                    <li
                      key={status}
                      className="flex items-center justify-between border-b border-line py-2.5 last:border-b-0"
                    >
                      <StatusTag status={status} />
                      <span className="tnum font-semibold text-ink">{count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <Panel title="Responsiveness">
              <dl className="text-sm">
                <div className="flex items-baseline justify-between border-b border-line py-2.5">
                  <dt className="text-ink-muted">Response rate</dt>
                  <dd
                    className="tnum font-medium text-ink"
                    data-pending={dashboard.coverage.responseRate !== "live" ? "true" : undefined}
                  >
                    {dashboard.responseRatePct === null ? "—" : `${dashboard.responseRatePct}%`}
                  </dd>
                </div>
                <div className="flex items-baseline justify-between py-2.5">
                  <dt className="text-ink-muted">Average reply time</dt>
                  <dd
                    className="tnum font-medium text-ink"
                    data-pending={dashboard.coverage.responseRate !== "live" ? "true" : undefined}
                  >
                    {dashboard.averageResponseMins === null
                      ? "—"
                      : `${dashboard.averageResponseMins} min`}
                  </dd>
                </div>
              </dl>
              <p className="mt-4 text-xs leading-relaxed text-ink-muted">
                Response times are recorded by the chat module, which is not built yet.
              </p>
            </Panel>
          </div>
        </div>
      ) : null}
    </PageShell>
  );
}
